
import { mixins } from "vue-class-component";
import { makeNameQuery } from "@/helpers/queryHelperFunctions";
import { cloneDeep, isEqual } from "lodash-es";

import LIST_DIRECTORY from "@/apollo/directory/ListDirectorySimple.gql";
import { Vue, Component, Prop, Watch } from "vue-property-decorator";

import Breadcrumbs from "@/components/breadcrumbs/BreadcrumbsNew.vue";
import ContractorCollectionFilters from "@/components/companyContractors/ContractorCollectionFiltersNew.vue";
import DefaultContentLayout from "@/components/scaffold/DefaultContentLayoutNew.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import InvitationSideBar from "@/components/companyContractors/invitationsidebarNew.vue";

import ActionPanel from "@/components/common/ActionsPanel.vue";

import {
  getFiltersDataFromQuery,
  joinQueryValues,
} from "@/helpers/router/prepareFiltersQuery";
import {
  getPaginationDataFromQuery,
  getPaginationQuery,
} from "@/helpers/router/preparePaginationQuery";
import NavGrayArrow from "@/components/icons//NavGrayArrow.vue";
import paginationConfig from "@/data/paginationConfig";
import contractorsCollectionFiltersTemplate from "@/data/filtersTemplates/contractorsCollectionFiltersTemplate";

import UiButton from "@/components/button/ui-button.vue";
import PlusIcon from "@/components/icons/PlusIconNew.vue";
import {
  Company,
  Contractor,
  User,
  Contractor_Bool_Exp,
  User_Profile_Bool_Exp,
} from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import Roles from "@/entities/Roles";
import PaginationConfig from "@/entities/PaginationConfig";
import ContractorsCollectionFiltersQuery from "@/entities/filters/ContractorsCollectionFiltersQuery";
import { getEmptyFiltersResultMessage } from "@/helpers/lists/emptyListMessagesNew";
import CompanyContractorsCollectionCard from "@/components/companyContractors/CompanyContractorsCollectionCard.vue";
import ElementsSizeProvider from "@/components/uiSize/ElementsSizeProvider.vue";
import CardsContainer from "@/components/card/CardsContainer.vue";
import EmptyListImageContractors from "@/components/icons/emptyListImages/EmptyListImageContractors.vue";
import getStateQueryConditions from "@/helpers/locations/stateQueryConditions";
import { use } from "vue/types/umd";

@Component({
  components: {
    EmptyListImageContractors,
    NavGrayArrow,
    CardsContainer,
    ElementsSizeProvider,
    CompanyContractorsCollectionCard,
    ContractorCollectionFilters,
    DefaultContentLayout,
    CardPagination,
    EmptyListPlaceholder,
    LoadingOverlay,
    Breadcrumbs,
    PlusIcon,
    UiButton,
    InvitationSideBar,
    ActionPanel,
  },
  apollo: {
    contractors: {
      query: LIST_DIRECTORY,
      variables() {
        return this.directoryFilter;
      },
      skip() {
        return this.skipQuery;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.contractor.length;
        this.$emit("contractorsTotalChanged", this.paginationConfig.total);
        this.managerContractors = data.contractor.filter(
          (item) => item.user.contracts.length > 0 && (!item.user.contracts[0].deleted && item.user.contracts[0]?.managers[0]?.approved)
        );
         const sortContarctor =  data.contractor.sort((a, b) => {
          const dateA = new Date(a.user.favorited_by_users[0].created_at); 
          const dateB = new Date(b.user.favorited_by_users[0].created_at); 
          return dateB.getTime() - dateA.getTime();
    });
    return sortContarctor;
      },
    },
  },
})
export default class MyFavorites extends Vue {
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Boolean, default: false })
  readonly favoriteContractors!: boolean;
  @Prop({ type: Boolean, default: false }) readonly canAddProject!: boolean;
  @Prop({ type: Boolean, default: false }) readonly managerDatabase!: boolean;

  contractors: Contractor[] | undefined = undefined;
  managerContractors: Contractor[] | undefined = undefined;

  paginationConfig: PaginationConfig = { ...paginationConfig };

  directoryFilters: ContractorsCollectionFiltersQuery = cloneDeep(
    contractorsCollectionFiltersTemplate
  );

  containerClasses: string = "h-full";

  get isManagerDatabase(): boolean {
    return this.$route.name === RouteNames.MANAGER_CONTRACTOR_DATABASE;
  }

  get managerHasAccess(): boolean {
    return this.isManagerDatabase || this.favoriteContractors;
  }

  get empty(): boolean {
    if (!this.contractors) {
      return false;
    }
    return this.contractors.length === 0;
  }

  get hasData(): boolean {
    return Boolean(this.contractors);
  }

  get invitationButtonShown(): boolean {
    return !this.favoriteContractors && this.filtersEmpty;
  }

  get filtersEmpty(): boolean {
    return isEqual(this.directoryFilters, contractorsCollectionFiltersTemplate);
  }

  get emptyListPlaceholder(): string {
    if (this.favoriteContractors) {
      if (this.filtersEmpty) {
        return "You have no favorite resources at the moment, but you might like someone in the Talent Directory";
      }

      return getEmptyFiltersResultMessage("favorite resources");
    }

    if (this.filtersEmpty) {
      return "You currently do not have any Favorited Resources";
    }

    return getEmptyFiltersResultMessage("resources");
  }

  // get emptyListSubtitle(): string{
  //   return "Start adding contractors to your Talent Directory";
  // }

  get loading(): boolean {
    return this.$apollo.queries.contractors.loading;
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  get currentUserManagerId(): string | undefined {
    return this.currentUser?.role === Roles.MANAGER
      ? this.currentUser.manager?.id
      : undefined;
  }

  get isResourcesPage(): boolean {
    return this.$route.path.includes("/company_database");
  }

  get directoryFilter(): any {
    const { page, limit } = this.paginationConfig;

    const {
      countries,
      states,
      cities,
      titles,
      skills,
      managers,
      projects,
      name,
      available,
      minRate,
      maxRate,
    } = this.directoryFilters;

    const nextOffset = page * limit - limit;
    const filterConditions: Contractor_Bool_Exp[] = [
      {
        approved: { _eq: true },
        deleted: { _eq: false },
        _or: [
          {
            user: {
              archived: {
                _is_null: true,
              },
            },
          },
          {
            user: {
              archived: {
                _eq: false,
              },
            },
          },
        ],
      },
    ];

    if (this.isManager) {
      const managerId = this.currentUser.manager!.id;

      filterConditions.push({
        managers: {
          _and: [
            { manager_id: { _eq: managerId } },
            { approved: { _eq: true } },
          ],
        },
         user: {
              contracts: {
                managers: {
                  approved: { _eq: true},
                },
              },
            },
      });
    }

    if (name) {
      filterConditions.push(makeNameQuery(name, ["user"]));
    }

    if (countries.length) {
      filterConditions.push({
        user: {
          profile: {
            country: { _in: countries },
          },
        },
      });
    }

    if (states.length) {
      filterConditions.push({
        user: {
          profile: {
            state: getStateQueryConditions({ countries, states }),
          },
        },
      });
    }

    if (cities.length) {
      filterConditions.push({
        user: {
          profile: {
            city: { _in: cities },
          },
        },
      });
    }

    if (available !== "all") {
      filterConditions.push({
        user: {
          available: { _eq: available === "yes" },
        },
      });
    }

    if (titles.length) {
      filterConditions.push({
        user: {
          profile: {
            job_title: { _in: titles },
          },
        },
      });
    }

    if (skills.length) {
      filterConditions.push({
        user: {
          profile: {
            skills: {
              skill_id: { _in: skills },
            },
          },
        },
      });
    }

    if (managers.length) {
      filterConditions.push({
        managers: {
          _and: [
            { manager_id: { _in: managers } },
            { approved: { _eq: true } },
          ],
        },
      });
    }

    if (projects.length) {
      filterConditions.push({
        project_activities: {
          project: {
            id: { _in: projects },
          },
        },
      });
    }

    if (minRate || maxRate) {
      const minConditions: User_Profile_Bool_Exp[] = [];
      const maxConditions: User_Profile_Bool_Exp[] = [];

      if (minRate) {
        minConditions.push({ min_rate: { _gte: minRate } });
        maxConditions.push({ max_rate: { _gte: minRate } });
      }
      if (maxRate) {
        minConditions.push({ min_rate: { _lte: maxRate } });
        maxConditions.push({ max_rate: { _lte: maxRate } });
      }

      if (minConditions.length && maxConditions.length) {
        filterConditions.push({
          user: {
            profile: {
              _or: [{ _and: minConditions }, { _and: maxConditions }],
            },
          },
        });
      }
    }

    return {
      offset: nextOffset,
      limit: limit ? limit : 50,
      where: {
        _and: filterConditions,
        user: {
          favorited_by_users: {
            favorited_by_id: {
              _eq: this.currentUser.id,
            },
          },
        },
      },
      id: this.currentUser.id,
    };
  }

  fetchNewRes(): void {
    this.$apollo.queries.contractors.refetch();
    window.location.reload();
  }
  get queryPropsToWatch(): string {
    return JSON.stringify([this.directoryFilters, this.paginationConfig]);
  }

  get queryParams(): {
    filters: ContractorsCollectionFiltersQuery;
    pagination: PaginationConfig;
  } {
    const query = this.$route.query;
    const { min_rate: minRate, max_rate: maxRate, ...queryParams } = query;

    const queryData = {
      minRate,
      maxRate,
      ...queryParams,
    };

    return {
      filters: getFiltersDataFromQuery<ContractorsCollectionFiltersQuery>(
        contractorsCollectionFiltersTemplate,
        queryData
      ),
      pagination: {
        ...this.paginationConfig,
        ...getPaginationDataFromQuery(query),
      },
    };
  }

  get isChosenPageEmpty(): boolean {
    const collectionEmpty = this.contractors?.length === 0;
    const notFirstPage = this.paginationConfig.page !== 1;

    return !this.loading && collectionEmpty && notFirstPage;
  }

  @Watch("queryPropsToWatch")
  updateQuery(): void {
    if (this.isChosenPageEmpty) {
      return this.showFirstPage();
    }

    const paginationQuery = getPaginationQuery(this.paginationConfig);

    const { minRate, maxRate, ...filtersData } = this.directoryFilters;

    const query = joinQueryValues({
      ...paginationQuery,
      ...filtersData,
      min_rate: minRate,
      max_rate: maxRate,
    });

    if (isEqual(query, this.$route.query)) {
      return;
    }

    this.$router.push({ query }).catch(() => {});
  }

  @Watch("$route.query", { immediate: true })
  onQueryChange(): void {
    const { filters, pagination } = this.queryParams;

    this.directoryFilters = filters;
    this.paginationConfig = pagination;
  }

  mounted(): void {
    if (!!this.company) {
      this.$apollo.queries.contractors.skip = false;
    }
  }

  showFirstPage(): void {
    this.paginationConfig = {
      ...this.paginationConfig,
      page: paginationConfig.page,
    };
  }

  managerAccessApproved(contractor: Contractor): boolean {
    return Boolean(contractor?.user.contracts[0]?.managers[0]?.approved);
  }

  updateFilters(updatedFilters: ContractorsCollectionFiltersQuery): void {
    const dataEqual = isEqual(this.directoryFilters, updatedFilters);

    if (dataEqual) {
      return;
    }

    const { minRate, maxRate, ...filtersData } = updatedFilters;

    const query = joinQueryValues({
      ...filtersData,
      min_rate: minRate,
      max_rate: maxRate,
    });

    this.$router.push({ query }).catch(() => {});
  }

  resetFilters(): void {
    if (isEqual(this.directoryFilters, contractorsCollectionFiltersTemplate)) {
      return;
    }

    this.directoryFilters = cloneDeep(contractorsCollectionFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

  message(userId: string): void {
    this.$router.push({
      name: RouteNames.INBOX,
      query: {
        recipientUserId: userId,
      },
    });
  }

  handleRefetch(): void {
    this.$apollo.queries.contractors.refetch();
    this.$emit("refetchRequests");
    this.$emit("refetchRequestsContractorsNumber");
  }

  prepareFilters(filterValues: Record<string, any>): void {
    this.directoryFilters = {
      ...this.directoryFilters,
      ...filterValues,
    };
  }
}
